import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AlertTriangle, Clock3, PenLine, PlusCircle, Users } from "lucide-react";
import { api, fmtFecha } from "@/lib/api";
import { hoyISOBA } from "@/components/ClockBA";
import { Avatar, Badge, Card, Kpi, MesNav, PageHeader, inputCls } from "@/components/rrhh/ui";

const fmtNum = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export default function LiquidacionMesPage() {
  const navigate = useNavigate();
  const [mes, setMes] = useState(hoyISOBA().slice(0, 7));
  const [localId, setLocalId] = useState("");

  const { data: locales } = useQuery({ queryKey: ["locales"], queryFn: async () => (await api.get("/locales")).data });
  const { data } = useQuery({
    queryKey: ["liq-resumen", mes, localId],
    queryFn: async () => (await api.get("/liquidacion/resumen", {
      params: { mes, local_id: localId || undefined },
    })).data,
  });

  const k = data?.kpis;
  const estadoMes = data?.estado_mes;
  const cerrado = estadoMes?.estado === "cerrado";

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-liquidacion">
      <PageHeader
        titulo="Liquidación del mes"
        subtitulo="Las horas de cada persona según sus fichadas y novedades. Lo que ajustes a mano queda marcado."
        testid="header-liquidacion"
      >
        <Badge color={cerrado ? "verde" : "gris"} testid="badge-estado-mes">
          {cerrado ? `Cerrado el ${fmtFecha(estadoMes.cerrado_en)}` : "Abierto"}
        </Badge>
      </PageHeader>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <MesNav mes={mes} onChange={setMes} testid="nav-mes-liq" />
        <select value={localId} onChange={(e) => setLocalId(e.target.value)} className={inputCls} data-testid="filtro-local-liq">
          <option value="">Todos los locales</option>
          {locales?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
        </select>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3.5 xl:grid-cols-4">
        <Kpi icon={Users} tone="verde" label="Personas" value={k?.personas ?? "—"} sub="En la liquidación del mes" testid="kpi-personas" />
        <Kpi icon={Clock3} tone="verde" label="Horas comunes totales" value={k ? fmtNum(k.horas_comunes) : "—"} sub="Suma del mes" testid="kpi-horas" />
        <Kpi icon={PlusCircle} tone="amarillo" label="Adicionales pagadas" value={k?.adicionales ?? "—"} sub="Días con la hora de 18 a 19" testid="kpi-adicionales" />
        <Kpi icon={AlertTriangle} tone={k && (k.pendientes.justificativos + k.pendientes.incompletas) > 0 ? "rojo" : "gris"}
             label="Pendientes" value={k ? k.pendientes.justificativos + k.pendientes.incompletas : "—"}
             sub={k ? `${k.pendientes.justificativos} justificativos sin resolver · ${k.pendientes.incompletas} jornada${k.pendientes.incompletas === 1 ? "" : "s"} incompleta${k.pendientes.incompletas === 1 ? "" : "s"}` : "…"}
             testid="kpi-pendientes" />
      </div>

      <Card titulo="Resumen por persona" subtitulo={`${data?.mes_label ?? ""} · tocá una fila para ver el detalle diario`} testid="card-resumen-persona" bodyCls="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3 font-semibold">Persona</th>
                <th className="px-3 py-3 text-right font-semibold">Días trab.</th>
                <th className="px-3 py-3 text-right font-semibold">Horas comunes</th>
                <th className="px-3 py-3 text-right font-semibold">Adicionales</th>
                <th className="px-3 py-3 text-right font-semibold">Tardanzas</th>
                <th className="px-3 py-3 text-right font-semibold">Faltas just.</th>
                <th className="px-3 py-3 text-right font-semibold">Faltas injust.</th>
                <th className="px-3 py-3 text-right font-semibold">Vacaciones</th>
                <th className="px-3 py-3 text-right font-semibold">Licencias</th>
                <th className="px-3 py-3 text-right font-semibold">Feriados/francos trab.</th>
                <th className="px-3 py-3 text-center font-semibold">Ajustes</th>
                <th className="px-5 py-3 font-semibold">Estado</th>
              </tr>
            </thead>
            <tbody>
              {data?.filas.map((f) => (
                <tr
                  key={f.empleado_id}
                  onClick={() => navigate(`/liquidacion/persona/${f.empleado_id}?mes=${mes}`)}
                  className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-[#2DD4BF]/[0.04]"
                  data-testid={`liq-fila-${f.legajo}`}
                >
                  <td className="min-w-44 px-5 py-3">
                    <span className="flex items-center gap-3">
                      <Avatar iniciales={f.iniciales} size="sm" />
                      <span className="whitespace-nowrap leading-tight">
                        <span className="block text-sm font-semibold text-white">{f.nombre}</span>
                        <span className="block font-mono-num text-[11px] text-gray-500">Legajo {f.legajo} · {f.local}</span>
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono-num text-gray-200">{f.dias_trab}</td>
                  <td className="px-3 py-3 text-right font-mono-num font-semibold text-white">{fmtNum(f.horas_comunes)}</td>
                  <td className={`px-3 py-3 text-right font-mono-num ${f.adicionales > 0 ? "text-gray-200" : "text-gray-600"}`}>{f.adicionales}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono-num">
                    {f.tardanzas > 0
                      ? <span className="text-amber-300">{f.tardanzas} · {f.tardanzas_min} min</span>
                      : <span className="text-gray-600">—</span>}
                  </td>
                  <td className={`px-3 py-3 text-right font-mono-num ${f.faltas_just > 0 ? "text-sky-300" : "text-gray-600"}`}>{f.faltas_just || "—"}</td>
                  <td className={`px-3 py-3 text-right font-mono-num ${f.faltas_injust > 0 ? "font-semibold text-red-300" : "text-gray-600"}`}>{f.faltas_injust || "—"}</td>
                  <td className={`px-3 py-3 text-right font-mono-num ${f.vacaciones > 0 ? "text-sky-300" : "text-gray-600"}`}>{f.vacaciones || "—"}</td>
                  <td className={`px-3 py-3 text-right font-mono-num ${f.licencias > 0 ? "text-fuchsia-300" : "text-gray-600"}`}>{f.licencias || "—"}</td>
                  <td className={`px-3 py-3 text-right font-mono-num ${f.feriados_francos > 0 ? "text-gray-200" : "text-gray-600"}`}>{f.feriados_francos}</td>
                  <td className="px-3 py-3 text-center">
                    {f.ajustes > 0
                      ? <span className="inline-flex items-center gap-1 font-mono-num font-semibold text-[#2DD4BF]"><PenLine size={12} /> {f.ajustes}</span>
                      : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    <Badge color={f.revisado ? "verde" : "gris"} testid={`estado-revision-${f.legajo}`}>{f.revisado ? "Revisado" : "Sin revisar"}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
            {data && (
              <tfoot>
                <tr className="border-t border-white/10 bg-white/[0.03] text-[13px] font-semibold" data-testid="liq-totales">
                  <td className="px-5 py-3 text-white">Totales</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{data.totales.dias_trab}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{fmtNum(data.totales.horas_comunes)}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{data.totales.adicionales}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-amber-300">{data.totales.tardanzas} · {data.totales.tardanzas_min} min</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{data.totales.faltas_just}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{data.totales.faltas_injust}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{data.totales.vacaciones}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{data.totales.licencias}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{data.totales.feriados_francos}</td>
                  <td className="px-3 py-3 text-center font-mono-num text-white">{data.totales.ajustes}</td>
                  <td className="px-5 py-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </motion.div>
  );
}
